import { useCallback, useMemo, useRef, useState } from 'react'
import { ChevronDown, FileText, Search } from 'lucide-react'
import { buildTree } from '../utils/pageTreeUtils'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { PageTreeNode } from '../utils/pageTreeUtils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  TreeExpander,
  TreeIcon,
  TreeLabel,
  TreeNode,
  TreeNodeContent,
  TreeNodeTrigger,
  TreeProvider,
  TreeView,
} from '@/components/kibo-ui/tree'
import { Icon, normalizeIconName } from '@/components/app/pickers/icon-picker'

interface BreadcrumbSubPagesDropdownProps {
  currentPageId: string
  projectId: string
  pages: Array<PageDocument>
  onNavigate: (slug: string) => void
}

/** Recursively filter a tree, keeping any node whose title matches or has a matching descendant. */
function filterTree(nodes: Array<PageTreeNode>, query: string): Array<PageTreeNode> {
  const lower = query.toLowerCase()
  const result: Array<PageTreeNode> = []
  for (const node of nodes) {
    const filteredChildren = filterTree(node.children, query)
    const titleMatch = (node.page.title || 'Untitled').toLowerCase().includes(lower)
    if (titleMatch || filteredChildren.length > 0) {
      result.push({ page: node.page, children: filteredChildren })
    }
  }
  return result
}

/** Collect all node IDs from a tree (used to expand all when searching). */
function collectIds(nodes: Array<PageTreeNode>): Array<string> {
  const ids: Array<string> = []
  for (const node of nodes) {
    ids.push(node.page.id)
    if (node.children.length > 0) {
      ids.push(...collectIds(node.children))
    }
  }
  return ids
}

function SubPageTreeNodes({
  nodes,
  onNavigate,
  level,
  parentPath,
  searchQuery,
}: {
  nodes: Array<PageTreeNode>
  onNavigate: (slug: string) => void
  level: number
  parentPath: Array<boolean>
  searchQuery: string
}) {
  return (
    <>
      {nodes.map((node, index) => {
        const hasChildren = node.children.length > 0
        const isLast = index === nodes.length - 1
        const iconName = normalizeIconName(node.page.icon)

        return (
          <TreeNode
            key={node.page.id}
            nodeId={node.page.id}
            level={level}
            isLast={isLast}
            parentPath={parentPath}
          >
            <TreeNodeTrigger
              className="py-1.5 px-2"
              onClick={(e) => {
                e.stopPropagation()
                onNavigate(node.page.slug)
              }}
            >
              <TreeExpander hasChildren={hasChildren} />
              <TreeIcon
                hasChildren={hasChildren}
                icon={
                  iconName
                    ? <Icon name={iconName} className="size-4" />
                    : <FileText className="size-4" />
                }
              />
              <TreeLabel>{node.page.title || 'Untitled'}</TreeLabel>
            </TreeNodeTrigger>
            {hasChildren && (
              <TreeNodeContent hasChildren={hasChildren}>
                <SubPageTreeNodes
                  nodes={node.children}
                  onNavigate={onNavigate}
                  level={level + 1}
                  parentPath={[...parentPath, isLast]}
                  searchQuery={searchQuery}
                />
              </TreeNodeContent>
            )}
          </TreeNode>
        )
      })}
    </>
  )
}

export function BreadcrumbSubPagesDropdown({
  currentPageId,
  projectId,
  pages,
  onNavigate,
}: BreadcrumbSubPagesDropdownProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const childTree = useMemo(() => {
    const active = pages.filter((p) => !p.isDeleted && p.projectId === projectId)
    return buildTree(active, currentPageId)
  }, [pages, currentPageId, projectId])

  const displayTree = useMemo(() => {
    if (!searchQuery.trim()) return childTree
    return filterTree(childTree, searchQuery.trim())
  }, [childTree, searchQuery])

  const expandedIds = useMemo(() => {
    // When searching, expand all nodes so matches are visible
    if (searchQuery.trim()) return collectIds(displayTree)
    // Default: expand top-level only
    return childTree.map((n) => n.page.id)
  }, [displayTree, childTree, searchQuery])

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) setSearchQuery('')
  }, [])

  if (childTree.length === 0) return null

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-center rounded hover:bg-accent/50 transition-colors p-0.5"
          aria-label="Show sub-pages"
        >
          <ChevronDown className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 max-h-80 p-0 flex flex-col"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <Search className="size-3.5 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {displayTree.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground">
              No pages found
            </div>
          ) : (
            <TreeProvider
              key={expandedIds.join(',')}
              defaultExpandedIds={expandedIds}
              showLines={false}
              showIcons
              selectable={false}
              indent={16}
              animateExpand={!searchQuery.trim()}
            >
              <TreeView className="p-1">
                <SubPageTreeNodes
                  nodes={displayTree}
                  onNavigate={(slug) => {
                    onNavigate(slug)
                    setOpen(false)
                  }}
                  level={0}
                  parentPath={[]}
                  searchQuery={searchQuery}
                />
              </TreeView>
            </TreeProvider>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
